import { Injectable } from '@angular/core';
import { SearchType, SeleccionadosBarraLateral } from '../../interfaces';
import { IRecorrido, IReporteBase, IUbicacion } from 'modelos/src';

@Injectable({
  providedIn: 'root',
})
export class ServiceGeneralVehiculos {
  constructor() {}

  // Sets memoizados por referencia del array de ids. shouldShowReport corre una
  // vez por reporte en cada recarga de markers: con includes() sobre arrays de
  // miles de ids el filtrado era O(reportes × ids).
  private setsPorArray = new WeakMap<string[], Set<string>>();

  private comoSet(ids: string[]): Set<string> {
    let set = this.setsPorArray.get(ids);
    if (!set) {
      set = new Set(ids);
      this.setsPorArray.set(ids, set);
    }
    return set;
  }

  /**
   * Filtro único de clientes (ya expandido con los hijos si `includeChildren`).
   * Sin clientes seleccionados no filtra.
   */
  private coincideCliente(
    idsClientes: string[] | undefined,
    idCliente: string | undefined,
  ): boolean {
    if (!idsClientes || idsClientes.length === 0) return true;
    return !!idCliente && this.comoSet(idsClientes).has(idCliente);
  }

  public shouldShowReport(
    reporte: IReporteBase<'Tracker 4G'>,
    index: number,
    seleccion?: SeleccionadosBarraLateral,
  ): boolean {
    // Usa la selección específica o la global
    const sel = seleccion;
    if (!sel?.filtrosSeleccionados) return true; // si no hay filtros, mostrar todo

    // Verificar si los filtros aplican a esta instancia
    const filtrosMapDefined =
      sel.mapaSeleccionado !== undefined && sel.mapaSeleccionado !== null;

    let filtersApplyToThisInstance = true;
    if (filtrosMapDefined) {
      const targetIdx = Math.max(0, (sel.mapaSeleccionado || 1) - 1);
      filtersApplyToThisInstance = index === targetIdx;
    }

    if (!filtersApplyToThisInstance) return true;

    // Filtro "solo encendidos": gate independiente de los demás filtros.
    // Encendido = valores.ignicion === true.
    if (sel.filtrosSeleccionados.soloEncendidos) {
      const ignicion = (reporte.valores as any)?.ignicion;
      if (ignicion !== true) return false;
    }

    const filtros = sel.filtrosSeleccionados;

    // Filtro de clientes: se evalúa contra el reporte, no contra la lista
    // precalculada, para que los vehículos que aparecen después (polling/WS)
    // también se muestren sin tener que reaplicar los filtros.
    if (
      !this.coincideCliente(
        filtros.idsClientesVehiculos,
        reporte.activo?.idCliente,
      )
    ) {
      return false;
    }

    // Verificar si hay algún otro filtro activo (grupos, funciones, tipos o vehículos específicos)
    const hayFiltrosActivos =
      (filtros.gruposVehiculos && filtros.gruposVehiculos.length > 0) ||
      (filtros.funcionVehiculos && filtros.funcionVehiculos.length > 0) ||
      (filtros.tiposVehiculo && filtros.tiposVehiculo.length > 0) ||
      (filtros.idsVehiculos && filtros.idsVehiculos.length > 0);

    // Si no hay filtros activos, mostrar todos los vehículos del cliente
    if (!hayFiltrosActivos) {
      return true;
    }

    // Hay filtros activos - verificar lista de vehículos filtrados
    const ids = sel.filtrosSeleccionados.idsVehiculosFiltrados;
    if (!ids || !Array.isArray(ids) || ids.length === 0) {
      // Hay filtros activos pero ningún vehículo coincide - ocultar
      return false;
    }

    const idsToShow = this.comoSet(ids);
    const asignados = reporte.idsAsignados || [];
    const activoId = reporte.activo?._id;

    // Mostrar si el activo._id está en el filtro O si alguno de los asignados está dentro del filtro
    return (
      (!!activoId && idsToShow.has(activoId)) ||
      asignados.some((id) => idsToShow.has(id))
    );
  }

  public shouldShowUbicacion(
    ubicacion: IUbicacion,
    index: number,
    seleccion?: SeleccionadosBarraLateral,
  ): boolean {
    // Usa la selección específica o la global
    const sel = seleccion;
    if (!sel?.filtrosSeleccionados) return true; // si no hay filtros, mostrar todo

    // Verificar si los filtros aplican a esta instancia
    const filtrosMapDefined =
      sel.mapaSeleccionado !== undefined && sel.mapaSeleccionado !== null;

    let filtersApplyToThisInstance = true;
    if (filtrosMapDefined) {
      const targetIdx = Math.max(0, (sel.mapaSeleccionado || 1) - 1);
      filtersApplyToThisInstance = index === targetIdx;
    }

    if (!filtersApplyToThisInstance) return true;

    const filtroClientes = sel.filtrosSeleccionados.idsClientesUbicaciones;
    const filtroUbicaciones = sel.filtrosSeleccionados.idsUbicaciones;

    // Filtro único de clientes, evaluado sobre la ubicación
    if (!this.coincideCliente(filtroClientes, ubicacion.idCliente)) {
      return false;
    }

    // Sin ubicaciones puntuales seleccionadas, mostrar todas las del cliente
    if (!filtroUbicaciones || filtroUbicaciones.length === 0) return true;

    const idUbicacion = ubicacion._id;
    if (!idUbicacion) return false;

    return filtroUbicaciones.includes(idUbicacion);
  }

  public shouldShowRecorrido(
    recorrido: IRecorrido,
    index: number,
    seleccion?: SeleccionadosBarraLateral,
  ): boolean {
    // Usa la selección específica o la global
    const sel = seleccion;
    if (!sel?.filtrosSeleccionados) return true; // si no hay filtros, mostrar todo

    // Verificar si los filtros aplican a esta instancia
    const filtrosMapDefined =
      sel.mapaSeleccionado !== undefined && sel.mapaSeleccionado !== null;

    let filtersApplyToThisInstance = true;
    if (filtrosMapDefined) {
      const targetIdx = Math.max(0, (sel.mapaSeleccionado || 1) - 1);
      filtersApplyToThisInstance = index === targetIdx;
    }

    if (!filtersApplyToThisInstance) return true;

    const filtroClientes = sel.filtrosSeleccionados.idsClientesRecorridos;
    const filtroRecorridos = sel.filtrosSeleccionados.idsRecorridos;

    // Filtro único de clientes, evaluado sobre el recorrido
    if (!this.coincideCliente(filtroClientes, recorrido.idCliente)) {
      return false;
    }

    // Sin recorridos puntuales seleccionados, mostrar todos los del cliente
    if (!filtroRecorridos || filtroRecorridos.length === 0) return true;

    const idRecorrido = recorrido._id;
    if (!idRecorrido) return false;

    return filtroRecorridos.includes(idRecorrido);
  }

  //LIMPIEZA
  public limpiarOtrasBusquedas(
    current: SeleccionadosBarraLateral,
    tipoBusquedaActual?: SearchType,
  ) {
    if (!tipoBusquedaActual) return;
    switch (tipoBusquedaActual) {
      case 'zona':
        current.puedeDibujarRecorrido = false;
        current.seleccionadosVehiculosRecorridos = {
          busquedaRealizada: null,
        } as any;
        break;

      case 'recorrido':
        current.puedeDibujar = false;
        current.seleccionadosVehiculoZona = {
          busquedaRealizada: null,
        } as any;
        break;
    }
  }
}
